/* eslint-disable complexity */
import * as React from 'react'
import { useMutation } from '@apollo/react-hooks'

import {
  EnterpriseContext,
  i18n,
  useBasketId,
  useHorizonSessionSettings,
  useTransmit,
} from '@thg-commerce/enterprise-core'
import { useBasket } from '@thg-commerce/enterprise-core/src/Basket/hooks/useBasketId'
import { useBackendEventNormaliser } from '@thg-commerce/enterprise-metrics'
import { pushToDataLayer } from '@thg-commerce/enterprise-metrics/src/data_layer'
import {
  SelectYourSampleMutationData,
  SelectYourSampleMutationVariables,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Mutation/AddSelectYourSampleProductToBasket'
import {
  RemoveYourSampleMutationData,
  RemoveYourSampleMutationVariables,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Mutation/RemoveSelectYourSampleProductFromBasket'
import { BasketData } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Basket/Basket'
import { ProductImage } from '@thg-commerce/gravity-elements'

import { AddSelectYourSampleProductToBasket as ADD_SELECT_YOUR_SAMPLE_MUTATION } from '../../../../graphql/Mutation/AddSelectYourSampleProductToBasket.graphql'
import { RemoveSelectYourSampleProductFromBasket as REMOVE_SELECT_YOUR_SAMPLE_MUTATION } from '../../../../graphql/Mutation/RemoveSelectYourSampleProductFromBasket.graphql'
import { useTheme } from '../../theme'
import { SelectYourSampleContext } from '../SelectYourSampleContext'

import {
  InfoIcon,
  Product as TierProduct,
  ProductContainer,
  ProductContentWrapper,
  ProductSubtitle,
  ProductTitle,
  SeeMoreDetails,
} from './styles'

export interface ProductProps {
  products: BasketData['selectYourSample'][0]['tiers'][0]['products']
  selectedProducts: { sku: number }[]
  tierId: string
  isQualified: boolean
  isSelectable: boolean
  enableGiftBasketLayout?: boolean
}

export const Product = (props: ProductProps) => {
  const [basketId, setBasketId] = useBasketId()
  const { basket } = useBasket()
  const sessionSettings = useHorizonSessionSettings()
  const theme = useTheme()
  const {
    selectYourSampleInteracted,
    setSelectYourSampleInteracted,
    informationModalPresenter,
  } = React.useContext(SelectYourSampleContext)

  const transmit = useTransmit()
  const normaliseBackendEvent = useBackendEventNormaliser()
  const { extensionsRef } = React.useContext(EnterpriseContext)
  const {
    value: [getExtensions],
  } = extensionsRef

  const [addSelectYourSampleProductToBasket] = useMutation<
    { addSelectYourSampleProductToBasket: SelectYourSampleMutationData },
    SelectYourSampleMutationVariables
  >(ADD_SELECT_YOUR_SAMPLE_MUTATION, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const resultExtensions =
        data?.addSelectYourSampleProductToBasket?.extensions || getExtensions()

      data?.addSelectYourSampleProductToBasket &&
        setBasketId(data.addSelectYourSampleProductToBasket.id)

      transmit({
        type: 'cart_interaction_event',
        payload: normaliseBackendEvent({
          basket,
          eventData: {
            type: 'cart_interaction_event',
            subtype: 'free_gift_add',
          },
          rays: resultExtensions?.ray ? [resultExtensions.ray] : undefined,
          experiments: resultExtensions?.experiments,
          requestData: {
            ...(resultExtensions?.LoggerLinkData || {
              start_timestamp: Date.now(),
              duration_ms: 0,
            }),
            url: window.location.href,
          },
        }),
      })
    },
  })

  const [removeSelectYourSampleProductToBasket] = useMutation<
    { removeSelectYourSampleProductFromBasket: RemoveYourSampleMutationData },
    RemoveYourSampleMutationVariables
  >(REMOVE_SELECT_YOUR_SAMPLE_MUTATION, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      data?.removeSelectYourSampleProductFromBasket &&
        setBasketId(data.removeSelectYourSampleProductFromBasket.id)
    },
  })

  const ariaLabel = i18n(
    'basket.selectyoursample.productinformation.aria.label',
  )

  const seeMoreDetailsText = i18n('basket.selectyoursample.seemoredetails.text')

  const productBlocks = props.products.map((product) => {
    const isSelected = props.selectedProducts
      .map((selectedProduct) => selectedProduct.sku)
      .includes(product.sku)
    const isDisabled =
      !props.isQualified ||
      (!isSelected && !props.isSelectable) ||
      !product.inStock

    const [selectYourSampleId, tierId] = props.tierId.split(':')

    const mutationVariables = {
      basketId,
      tierId,
      selectYourSampleId,
      sku: product.sku.toString(),
      settings: sessionSettings,
    }

    const onProductClick = () => {
      if (isDisabled) return

      !selectYourSampleInteracted && setSelectYourSampleInteracted(true)

      if (isSelected) {
        removeSelectYourSampleProductToBasket({
          variables: mutationVariables,
        })

        pushToDataLayer({
          type: 'elysiumEvent',
          eventData: {
            eventAction: 'Removing Item',
            eventCategory: 'freeProductSelection',
            eventLabel: 'Gift Id',
            eventLabelValue: `${product.sku}${selectYourSampleId}${tierId}`,
          },
        })

        pushToDataLayer({
          type: 'elysiumEvent',
          eventData: {
            eventAction: 'Removed Simple Item',
            eventCategory: 'freeProductSelection',
            eventLabel: 'Gift Id',
            eventLabelValue: `${product.sku}${selectYourSampleId}${tierId}`,
          },
        })
      }

      if (!isSelected) {
        addSelectYourSampleProductToBasket({
          variables: mutationVariables,
        })

        pushToDataLayer({
          type: 'elysiumEvent',
          eventData: {
            eventAction: 'Adding Item',
            eventCategory: 'freeProductSelection',
            eventLabel: 'Gift Id',
            eventLabelValue: `${product.sku}${selectYourSampleId}${tierId}`,
          },
        })

        pushToDataLayer({
          type: 'elysiumEvent',
          eventData: {
            eventAction: 'Added Simple Item',
            eventCategory: 'freeProductSelection',
            eventLabel: 'Gift Id',
            eventLabelValue: `${product.sku}${selectYourSampleId}${tierId}`,
          },
        })
      }
    }

    return (
      <TierProduct
        key={product.sku}
        data-testid={`product-button-${product.sku}`}
        isSelected={isSelected}
        aria-label={product.title}
        onClick={onProductClick}
        disabled={isDisabled}
        enableGiftBasketLayout={props.enableGiftBasketLayout}
      >
        <ProductImage
          alt=""
          urls={{
            largeProduct: props.enableGiftBasketLayout
              ? product.images?.[0]?.largeProduct ?? ''
              : product.images?.[0]?.thumbnail ?? '',
          }}
          width={props.enableGiftBasketLayout ? [100] : [73]}
          height={props.enableGiftBasketLayout ? [108] : []}
        />
        {props.enableGiftBasketLayout ? (
          <ProductContentWrapper>
            <ProductTitle
              enableGiftBasketLayout={props.enableGiftBasketLayout}
              textStyling={
                theme.pageTheme.selectYourSample?.productTitle?.textStyling
              }
              margin={theme.pageTheme.selectYourSample?.productTitle?.margin}
              title={product.title}
            >
              {product.title}
            </ProductTitle>
            {product.subtitle && (
              <ProductSubtitle
                enableGiftBasketLayout={props.enableGiftBasketLayout}
                textStyling={
                  theme.pageTheme.selectYourSample?.productSubtitle?.textStyling
                }
                margin={
                  theme.pageTheme.selectYourSample?.productSubtitle?.margin
                }
                title={product.subtitle}
              >
                {product.subtitle}
              </ProductSubtitle>
            )}
            <SeeMoreDetails
              enableGiftBasketLayout={props.enableGiftBasketLayout}
              textStyling={
                theme.pageTheme.selectYourSample?.seeMoreDetails?.textStyling
              }
              onClick={(e) => {
                pushToDataLayer({
                  type: 'elysiumEvent',
                  eventData: {
                    eventAction: 'Clicked on Product Info',
                    eventCategory: 'freeProductModal',
                    eventLabel: 'product Id',
                    eventLabelValue: `${product.sku}`,
                  },
                })
                informationModalPresenter.current &&
                  informationModalPresenter.current(product)
                e.stopPropagation()
              }}
            >
              {seeMoreDetailsText}
            </SeeMoreDetails>
          </ProductContentWrapper>
        ) : (
          <>
            <ProductTitle
              enableGiftBasketLayout={props.enableGiftBasketLayout}
              textStyling={
                theme.pageTheme.selectYourSample?.productTitle?.textStyling
              }
              margin={theme.pageTheme.selectYourSample?.productTitle?.margin}
            >
              {product.title}
            </ProductTitle>
            <InfoIcon
              ariaLabel={ariaLabel}
              onClick={(e) => {
                pushToDataLayer({
                  type: 'elysiumEvent',
                  eventData: {
                    eventAction: 'Clicked on Product Info',
                    eventCategory: 'freeProductModal',
                    eventLabel: 'product Id',
                    eventLabelValue: `${product.sku}`,
                  },
                })
                informationModalPresenter.current &&
                  informationModalPresenter.current(product)
                e.stopPropagation()
              }}
            />
          </>
        )}
      </TierProduct>
    )
  })

  return <ProductContainer>{productBlocks}</ProductContainer>
}
